import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  BookOpen, Upload, Brain, BarChart3, Search, FileText,
  Check, AlertTriangle, Loader2, RefreshCw, ChevronRight,
  Database, Zap, Eye,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Tab: Dictionary ─────────────────────────────────────────────────────────

const DictionaryTab = ({ getAuthHeaders }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchDictionary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/uw/admin/dictionary`, { headers: getAuthHeaders() });
      setEntries(res.data);
    } catch (err) { toast.error('Failed to load dictionary'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchDictionary(); }, [fetchDictionary]);

  const filtered = entries.filter(e =>
    e.placeholder_key.includes(search.toUpperCase()) ||
    e.label.toLowerCase().includes(search.toLowerCase())
  );

  const sourceGroups = {};
  entries.forEach(e => {
    sourceGroups[e.source_type] = (sourceGroups[e.source_type] || 0) + 1;
  });

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="dictionary-tab">
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(sourceGroups).sort().map(([src, count]) => (
          <Badge key={src} variant="outline" className="text-xs">{src}: {count}</Badge>
        ))}
        <Badge className="bg-emerald-100 text-emerald-800">Total: {entries.length}</Badge>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search placeholder key or label..."
            className="pl-8 h-9"
            data-testid="dictionary-search"
          />
        </div>
      </div>
      <div className="border rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left p-2 font-medium">Key</th>
              <th className="text-left p-2 font-medium">Label</th>
              <th className="text-left p-2 font-medium">Source</th>
              <th className="text-left p-2 font-medium">Format</th>
              <th className="text-left p-2 font-medium">Required</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.placeholder_key} className="border-t hover:bg-gray-50">
                <td className="p-2 font-mono text-gray-700">{e.placeholder_key}</td>
                <td className="p-2 text-gray-600">{e.label}</td>
                <td className="p-2"><Badge variant="outline" className="text-[10px]">{e.source_type}</Badge></td>
                <td className="p-2 text-gray-500">{e.format_type}</td>
                <td className="p-2">{e.required && <Check className="h-3 w-3 text-green-600" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Shared: Scan Results Display ────────────────────────────────────────────

const ScanResultsDisplay = ({ scanResult, title }) => {
  const statusIcon = (status) => {
    if (status === 'auto_mapped' || status === 'derived') return <Check className="h-3.5 w-3.5 text-green-600" />;
    if (status === 'needs_review') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
  };

  if (!scanResult) return null;

  const unmapped = scanResult.unmapped_list || scanResult.unmapped_placeholders || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> {title}
          <Badge className={`ml-auto text-xs ${scanResult.dictionary_coverage_pct >= 90 ? 'bg-emerald-100 text-emerald-800' : scanResult.dictionary_coverage_pct >= 70 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
            {scanResult.dictionary_coverage_pct}%
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
          <div className="bg-gray-50 rounded p-2 border">
            <div className="text-lg font-bold" data-testid="scan-total">{scanResult.total_found}</div>
            <div className="text-[10px] text-gray-500">Total</div>
          </div>
          <div className="bg-green-50 rounded p-2 border border-green-200">
            <div className="text-lg font-bold text-green-700" data-testid="scan-autofilled">{scanResult.autofilled_count}</div>
            <div className="text-[10px] text-green-600">Auto-filled</div>
          </div>
          <div className="bg-blue-50 rounded p-2 border border-blue-200">
            <div className="text-lg font-bold text-blue-700" data-testid="scan-intake">{scanResult.intake_count}</div>
            <div className="text-[10px] text-blue-600">Intake</div>
          </div>
          <div className="bg-purple-50 rounded p-2 border border-purple-200">
            <div className="text-lg font-bold text-purple-700" data-testid="scan-manual">{scanResult.manual_count}</div>
            <div className="text-[10px] text-purple-600">Manual</div>
          </div>
          <div className="bg-amber-50 rounded p-2 border border-amber-200">
            <div className="text-lg font-bold text-amber-700" data-testid="scan-review">{scanResult.needs_review ?? 0}</div>
            <div className="text-[10px] text-amber-600">Review</div>
          </div>
          <div className="bg-red-50 rounded p-2 border border-red-200">
            <div className="text-lg font-bold text-red-700" data-testid="scan-unmapped">{scanResult.unmapped_count}</div>
            <div className="text-[10px] text-red-600">Unmapped</div>
          </div>
        </div>

        {unmapped.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2" data-testid="scan-unmapped-list">
            <div className="text-xs font-medium text-red-700 mb-1">Unmapped Placeholders:</div>
            <div className="flex flex-wrap gap-1">
              {unmapped.map(k => (
                <Badge key={k} variant="destructive" className="text-[10px] font-mono">{k}</Badge>
              ))}
            </div>
          </div>
        )}

        <div className="border rounded-lg overflow-hidden max-h-[40vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left p-2 font-medium w-8"></th>
                <th className="text-left p-2 font-medium">Placeholder</th>
                <th className="text-left p-2 font-medium">Matched To</th>
                <th className="text-left p-2 font-medium">Source</th>
                <th className="text-left p-2 font-medium">Method</th>
                <th className="text-left p-2 font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {scanResult.results.map(r => (
                <tr key={r.normalized_key} className="border-t hover:bg-gray-50">
                  <td className="p-2">{statusIcon(r.status)}</td>
                  <td className="p-2 font-mono">
                    {r.normalized_key}
                    {r.is_alias && <span className="ml-1 text-[9px] bg-orange-100 text-orange-700 px-1 rounded">alias</span>}
                  </td>
                  <td className="p-2 font-mono text-emerald-700">{r.canonical_key || '—'}</td>
                  <td className="p-2">
                    <Badge variant="outline" className="text-[10px]">{r.source_type}</Badge>
                  </td>
                  <td className="p-2"><Badge variant="outline" className="text-[10px]">{r.match_method}</Badge></td>
                  <td className="p-2">
                    <span className={`font-medium ${r.confidence >= 90 ? 'text-green-600' : r.confidence >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                      {r.confidence?.toFixed(0)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Tab: Template Scanner ───────────────────────────────────────────────────

const ScannerTab = ({ getAuthHeaders }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // DOCX upload state
  const [docxFile, setDocxFile] = useState(null);
  const [uploadScanning, setUploadScanning] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // Register state
  const [registerTemplateId, setRegisterTemplateId] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/uw/templates`, { headers: getAuthHeaders() });
      setTemplates(res.data || []);
    } catch (err) { toast.error('Failed to load templates'); }
    finally { setLoadingTemplates(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const runAnalysis = useCallback(async (templateId) => {
    if (!templateId) return;
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.post(
        `${API}/uw/scan/analyze/${templateId}`,
        {},
        { headers: getAuthHeaders() }
      );
      setScanResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  }, [getAuthHeaders]);

  const handleTemplateChange = useCallback((e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    setScanResult(null);
    if (id) runAnalysis(id);
  }, [runAnalysis]);

  const handleDocxUploadScan = async () => {
    if (!docxFile) return;
    setUploadScanning(true);
    setUploadResult(null);
    setRegisterResult(null);
    try {
      const fd = new FormData();
      fd.append('file', docxFile);
      const res = await axios.post(`${API}/uw/scan/scan-docx-upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      toast.success(`Found ${res.data.total_found} placeholders in ${res.data.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'DOCX scan failed');
    } finally { setUploadScanning(false); }
  };

  const handleRegister = async () => {
    if (!uploadResult || !uploadResult.placeholders_found?.length) return;
    setRegistering(true);
    setRegisterResult(null);
    try {
      // Build canonical_map from scan results: normalized_key → canonical_key
      const canonicalMap = {};
      (uploadResult.results || []).forEach(r => {
        if (r.normalized_key && r.canonical_key) {
          canonicalMap[r.normalized_key] = r.canonical_key;
        }
      });
      const payload = {
        placeholders: uploadResult.placeholders_found,
        template_id: registerTemplateId || null,
        canonical_map: canonicalMap,
      };
      const res = await axios.post(`${API}/uw/scan/register-placeholders`, payload, {
        headers: getAuthHeaders(),
      });
      setRegisterResult(res.data);
      toast.success(`Registered ${res.data.newly_added} new + ${res.data.already_known} known placeholders`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setRegistering(false); }
  };

  const templateTypeLabel = (tpl) => {
    const t = tpl.template_type || 'google_doc';
    if (t === 'pdf_acroform') return 'PDF';
    if (t === 'docx') return 'DOCX';
    return 'Google Doc';
  };

  if (loadingTemplates) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="scanner-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4" /> Read-Only Template Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Select Template</Label>
            <select
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              className="w-full h-9 rounded border border-gray-300 text-sm px-2"
              data-testid="scan-template-select"
            >
              <option value="">-- Choose a template --</option>
              {templates.map(tpl => (
                <option key={tpl.id} value={tpl.id}>
                  [{tpl.company}] {tpl.name} ({templateTypeLabel(tpl)})
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1 border" data-testid="scan-template-info">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{selectedTemplate.name}</span>
                <Badge variant="outline" className="text-[10px]">{templateTypeLabel(selectedTemplate)}</Badge>
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <span>Company: <strong>{selectedTemplate.company}</strong></span>
                <span>Tile: <strong>{selectedTemplate.tile}</strong></span>
                {selectedTemplate.google_doc_id && (
                  <a
                    href={`https://docs.google.com/document/d/${selectedTemplate.google_doc_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                    data-testid="scan-drive-link"
                  >
                    <FileText className="h-3 w-3" /> Open in Drive
                  </a>
                )}
              </div>
              {selectedTemplate.google_doc_id && (
                <div className="text-gray-400 font-mono text-[10px] truncate">
                  ID: {selectedTemplate.google_doc_id}
                </div>
              )}
            </div>
          )}

          {scanning && (
            <div className="text-center py-4">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-1" />
              <span className="text-xs text-gray-500">Analyzing placeholders...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <ScanResultsDisplay scanResult={scanResult} title={`Coverage: ${scanResult?.template_name || ''}`} />

      {/* ─── DOCX Upload Scanner ─── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" /> Upload DOCX for Scan (Read-Only)
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Diagnostic only — no file saved, no template created, no database writes.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">DOCX File</Label>
              <Input
                type="file"
                accept=".docx"
                onChange={e => { setDocxFile(e.target.files[0]); setUploadResult(null); }}
                className="h-9 text-sm"
                data-testid="docx-upload-input"
              />
            </div>
            <Button
              onClick={handleDocxUploadScan}
              disabled={uploadScanning || !docxFile}
              size="sm"
              data-testid="docx-upload-scan-btn"
            >
              {uploadScanning
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Scanning...</>
                : <><Zap className="h-4 w-4 mr-1" />Scan</>
              }
            </Button>
          </div>
          {docxFile && !uploadScanning && !uploadResult && (
            <div className="text-xs text-gray-500">Selected: {docxFile.name}</div>
          )}
        </CardContent>
      </Card>

      <ScanResultsDisplay scanResult={uploadResult} title={`Upload Scan: ${uploadResult?.filename || ''}`} />

      {/* ─── Register Placeholders Panel ─── */}
      {uploadResult && uploadResult.total_found > 0 && (
        <Card data-testid="register-placeholders-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-emerald-600" />
              Register Placeholders to System
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Persist the <strong>{uploadResult.placeholders_found?.length || uploadResult.total_found}</strong> scanned placeholders into the dictionary. Optionally link to an existing template.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Link to Existing Template (optional)</Label>
              <select
                value={registerTemplateId}
                onChange={e => { setRegisterTemplateId(e.target.value); setRegisterResult(null); }}
                className="w-full h-9 rounded border border-gray-300 text-sm px-2 mt-1"
                data-testid="register-template-select"
              >
                <option value="">-- No template link --</option>
                {templates.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>
                    [{tpl.company}] {tpl.name}
                  </option>
                ))}
              </select>
            </div>

            <Button
              onClick={handleRegister}
              disabled={registering}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="register-placeholders-btn"
            >
              {registering
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Registering...</>
                : <><Database className="h-4 w-4 mr-2" />Register Placeholders to System</>
              }
            </Button>

            {registerResult && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="register-result">
                <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                  <Check className="h-4 w-4" /> Registration Complete
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white rounded border p-2 text-center">
                    <div className="text-lg font-bold text-emerald-700" data-testid="register-newly-added">{registerResult.newly_added}</div>
                    <div className="text-gray-500">Newly Added</div>
                  </div>
                  <div className="bg-white rounded border p-2 text-center">
                    <div className="text-lg font-bold text-gray-600" data-testid="register-already-known">{registerResult.already_known}</div>
                    <div className="text-gray-500">Already Known</div>
                  </div>
                </div>
                {registerResult.template_updated && (
                  <div className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 rounded px-2 py-1" data-testid="register-template-updated">
                    <ChevronRight className="h-3 w-3" />
                    Template placeholders updated — {registerResult.total_processed} keys confirmed
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Tab: Learning Center ────────────────────────────────────────────────────

const LearningTab = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('PFS');

  const fetchMemory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/uw/admin/memory?company=${company}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) { toast.error('Failed to load memory'); }
    finally { setLoading(false); }
  }, [getAuthHeaders, company]);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  const tm = data?.tenant_memory || [];
  const sb = data?.shared_brain || [];

  return (
    <div className="space-y-5" data-testid="learning-tab">
      <div className="flex items-center gap-3">
        <select value={company} onChange={e => setCompany(e.target.value)} className="h-8 rounded border border-gray-300 text-sm px-2" data-testid="memory-company">
          <option value="PFS">PFS</option>
          <option value="DIB">DIB</option>
        </select>
        <Button size="sm" variant="outline" onClick={fetchMemory}><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
        <Badge className="bg-emerald-100 text-emerald-800">{data?.tenant_stats?.total_mappings || 0} tenant mappings</Badge>
        <Badge className="bg-blue-100 text-blue-800">{data?.shared_stats?.total_shared_mappings || 0} shared brain entries</Badge>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" /> Tenant Memory ({company})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tm.length === 0 ? (
            <p className="text-sm text-gray-500">No mappings learned yet. Scan templates and confirm mappings to build memory.</p>
          ) : (
            <div className="border rounded max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Raw Key</th>
                    <th className="text-left p-2 font-medium">Canonical</th>
                    <th className="text-left p-2 font-medium">Confirmed</th>
                    <th className="text-left p-2 font-medium">Confidence</th>
                    <th className="text-left p-2 font-medium">Promoted</th>
                  </tr>
                </thead>
                <tbody>
                  {tm.map(m => (
                    <tr key={m.normalized_key} className="border-t">
                      <td className="p-2 font-mono">{m.normalized_key}</td>
                      <td className="p-2 font-mono text-emerald-700">{m.canonical_key}</td>
                      <td className="p-2 text-center">{m.times_confirmed}</td>
                      <td className="p-2">{m.confidence_score?.toFixed(0)}%</td>
                      <td className="p-2">{m.promoted ? <Check className="h-3 w-3 text-green-600" /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-4 w-4" /> Shared Brain (PFS + DIB)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sb.length === 0 ? (
            <p className="text-sm text-gray-500">Shared brain is empty. Mappings auto-promote after 3 confirmations at 85%+ confidence.</p>
          ) : (
            <div className="border rounded max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Normalized Key</th>
                    <th className="text-left p-2 font-medium">Canonical</th>
                    <th className="text-left p-2 font-medium">Confirmations</th>
                    <th className="text-left p-2 font-medium">From Tenant</th>
                  </tr>
                </thead>
                <tbody>
                  {sb.map(m => (
                    <tr key={m.normalized_key} className="border-t">
                      <td className="p-2 font-mono">{m.normalized_key}</td>
                      <td className="p-2 font-mono text-emerald-700">{m.canonical_key}</td>
                      <td className="p-2 text-center">{m.total_confirmations}</td>
                      <td className="p-2">{m.created_from_tenant}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Tab: Coverage Audit ─────────────────────────────────────────────────────

const CoverageTab = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('PFS');
  const [expanded, setExpanded] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/uw/admin/coverage-audit?company=${company}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) { toast.error('Failed to load audit'); }
    finally { setLoading(false); }
  }, [getAuthHeaders, company]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="coverage-tab">
      <div className="flex items-center gap-3">
        <select value={company} onChange={e => setCompany(e.target.value)} className="h-8 rounded border border-gray-300 text-sm px-2" data-testid="audit-company">
          <option value="PFS">PFS</option>
          <option value="DIB">DIB</option>
        </select>
        <Button size="sm" variant="outline" onClick={fetchAudit}><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{data.avg_coverage_pct}%</p>
              <p className="text-xs text-gray-500">Avg Coverage</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold">{data.total_templates}</p>
              <p className="text-xs text-gray-500">Templates</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{data.total_auto_fill}</p>
              <p className="text-xs text-gray-500">Auto-fill Fields</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{data.total_unmapped}</p>
              <p className="text-xs text-gray-500">Unmapped</p>
            </Card>
          </div>

          <div className="space-y-2">
            {(data.templates || []).map(tpl => (
              <Card key={tpl.template_id} className="overflow-hidden">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpanded(expanded === tpl.template_id ? null : tpl.template_id)}
                >
                  <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium text-sm flex-1 truncate">{tpl.template_name || tpl.template_id}</span>
                  <Badge className={`text-xs ${tpl.auto_fill_pct >= 90 ? 'bg-green-100 text-green-800' : tpl.auto_fill_pct >= 70 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                    {tpl.auto_fill_pct}% auto
                  </Badge>
                  <span className="text-xs text-gray-500">{tpl.total_placeholders} fields</span>
                  {tpl.unmapped_count > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{tpl.unmapped_count} unmapped</Badge>}
                  <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${expanded === tpl.template_id ? 'rotate-90' : ''}`} />
                </div>
                {expanded === tpl.template_id && tpl.field_details && (
                  <div className="border-t px-3 pb-3">
                    <table className="w-full text-xs mt-2">
                      <thead>
                        <tr>
                          <th className="text-left p-1 font-medium">Placeholder</th>
                          <th className="text-left p-1 font-medium">Source</th>
                          <th className="text-left p-1 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tpl.field_details.map(f => (
                          <tr key={f.key} className="border-t">
                            <td className="p-1 font-mono">{f.key}</td>
                            <td className="p-1"><Badge variant="outline" className="text-[10px]">{f.source_type}</Badge></td>
                            <td className="p-1">
                              {f.status === 'auto' && <Badge className="bg-green-100 text-green-700 text-[10px]">auto</Badge>}
                              {f.status === 'intake' && <Badge className="bg-amber-100 text-amber-700 text-[10px]">intake</Badge>}
                              {f.status === 'manual' && <Badge className="bg-purple-100 text-purple-700 text-[10px]">manual</Badge>}
                              {f.status === 'unmapped' && <Badge className="bg-red-100 text-red-700 text-[10px]">unmapped</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Admin Page ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'dictionary', label: 'Dictionary', icon: BookOpen },
  { id: 'scanner', label: 'Template Scanner', icon: Search },
  { id: 'learning', label: 'Learning Center', icon: Brain },
  { id: 'coverage', label: 'Coverage Audit', icon: BarChart3 },
];

export default function UWAdmin() {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('dictionary');

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6" data-testid="uw-admin-page">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-2xl font-bold">UW Toolkit Admin</h1>
          <Badge className="bg-blue-100 text-blue-800">Intelligent Document Engine</Badge>
        </div>

        <div className="flex gap-1 border-b mb-6">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'dictionary' && <DictionaryTab getAuthHeaders={getAuthHeaders} />}
        {activeTab === 'scanner' && <ScannerTab getAuthHeaders={getAuthHeaders} />}
        {activeTab === 'learning' && <LearningTab getAuthHeaders={getAuthHeaders} />}
        {activeTab === 'coverage' && <CoverageTab getAuthHeaders={getAuthHeaders} />}
      </div>
    </Layout>
  );
}
